import React, {useEffect} from "react";
import "./tournament-players-unassigned-add-edit.scss";
import {useMatch, useNavigate} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import {GSForm, GSSidePanelPage} from "golfstatus_react_components";
import {faChevronLeft} from "@fortawesome/free-solid-svg-icons";
import {
  clearNotifications,
  createUnassignedPlayer, getTournamentUnassignedPlayers, getUnassignedPlayer, restoreUnassignedPlayer,
  selectCurrentUnassignedPlayer,
  selectTeamsNotifications,
  setCurrentUnassignedPlayer, updateUnassignedPlayer,
} from "../../../reducers/teamsSlice";
import {useFormValidation} from "../../../hooks/formHooks";
import {getSaveBannerActions, useNotificationBanner, useNotificationNavigation} from "../../../hooks/notificationHooks";
import {getAddPlayerForm} from "../../../forms/TeamForm";
import {selectCurrentTournament} from "../../../reducers/tournamentSlice";


const TournamentPlayersUnassignedAddEdit = (props) => {

  // hooks
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const matchEdit = useMatch("/:tournamentID/teams/unassigned-players/:playerID/edit");
  const matchAdd = useMatch("/:tournamentID/teams/unassigned-players/add");

  // selectors
  const currentUnassignedPlayer = useSelector(selectCurrentUnassignedPlayer);
  const currentTournament = useSelector(selectCurrentTournament);
  const teamNotifications = useSelector(selectTeamsNotifications);

  // form context
  const [context, isValid, setIsValid] = useFormValidation(
    currentUnassignedPlayer && matchAdd
  );

  context.submit = () => {
    setIsValid(false);
    setUnsaved(false);
    let updatedPlayer = { ...currentUnassignedPlayer };

    if (matchEdit?.params?.playerID) {
      dispatch(updateUnassignedPlayer(updatedPlayer));
    } else {
      updatedPlayer.tournament = currentTournament;
      dispatch(createUnassignedPlayer(updatedPlayer));
    }
  };

  context.updatePlayer = (value, property) => {
    setIsValid(true);
    let updatePlayer = { ...currentUnassignedPlayer };
    updatePlayer[property] = value;
    if (property === "firstName" || property === "lastName") {
      updatePlayer["name"] = `${updatePlayer.firstName} ${updatePlayer.lastName}`;
    }
    dispatch(setCurrentUnassignedPlayer(updatePlayer));
  };

  context.isProAmTourney = currentTournament?.singleFormat === "pro_am";

  context.isPro = currentUnassignedPlayer?.flag === "pro";

  context.togglePro = () => {
    if (currentUnassignedPlayer.firstName && currentUnassignedPlayer.lastName) {
      setIsValid(true);
    }

    if (currentUnassignedPlayer?.flag === "pro") {
      dispatch(setCurrentUnassignedPlayer({
        ...currentUnassignedPlayer,
        flag: "default",
      }));
    } else {
      dispatch(setCurrentUnassignedPlayer({
        ...currentUnassignedPlayer,
        flag: "pro",
      }));
    }
  };

  // effects
  useEffect(() => {
    if (matchEdit?.params?.playerID && matchEdit?.params?.playerID !== currentUnassignedPlayer?.id) {
      dispatch(getUnassignedPlayer(matchEdit.params.playerID))
    }
  }, [dispatch, matchEdit?.params?.playerID, currentUnassignedPlayer?.id]);

  const removePlayer = () => {
    navigate(matchEdit.pathname.replace('edit', 'remove'));
  };

  const restorePlayer = async () => {
    await dispatch(restoreUnassignedPlayer(currentUnassignedPlayer));
    dispatch(getTournamentUnassignedPlayers(matchEdit?.params?.tournamentID));
  };

  const leftNavigation = () => {
    if (isValid && !bannerNotifications) {
      setUnsaved(true);
      return;
    }
    dispatch(clearNotifications());
    navigate(-1);
  };

  const getNavigation = () => {
    return {
      title: `Player Details`,
      leftIcon: faChevronLeft,
      leftButtonClick: leftNavigation,
    };
  };

  const timeoutAction = () => {
    dispatch(clearNotifications());
  };

  // custom hooks
  useNotificationNavigation(
    "created",
    teamNotifications,
    matchAdd?.pathname?.replace("add", `${currentUnassignedPlayer?.id}/edit`)
  );

  let notificationSettings = {
    notifications: teamNotifications,
    saveAction: context.submit,
    bannerActions: getSaveBannerActions(context.submit, leftNavigation),
    timeoutAction,
  };

  const [bannerNotifications, setUnsaved] = useNotificationBanner(
    notificationSettings
  );

  const getContent = () => {
    return (
      <GSForm
        formTitle={matchAdd ? "Add Unassigned Player" : "Edit Unassigned Player"}
        formSections={getAddPlayerForm(currentUnassignedPlayer, context)}
      ></GSForm>
    );
  };

  const getDrawerActions = () => {
    let actions = [
      {
        name: "Save",
        isDisabled: !isValid,
        action: context.submit,
        type: "black",
      },
      { name: "cancel", action: leftNavigation, type: "grey" },
    ];
    if (matchEdit) {
      if (currentUnassignedPlayer.status === "active") {
        actions.push({
          name: "Remove Player",
          action: removePlayer,
          type: "transparent red",
        });
      } else {
        actions.push({
          name: "Restore Player",
          action: restorePlayer,
          type: "green",
        });
      }

    }
    return actions;
  };

  const getDrawer = () => {
    return {
      actions: getDrawerActions(),
    };
  };

  return (
    <tournament-players-unassigned-add-edit>
      <GSSidePanelPage
        header={getNavigation()}
        banner={bannerNotifications}
        content={getContent()}
        drawer={getDrawer()}
      ></GSSidePanelPage>
    </tournament-players-unassigned-add-edit>
  );
};

export default TournamentPlayersUnassignedAddEdit;
